import * as Tooltip from "@radix-ui/react-tooltip";
import { ArrowLeft, ChevronDown } from "lucide-react";
import React from "react";
import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { formatDuration } from "../../utils";
import { INSIGNIFICANT_COLOR } from "./palette";
import { sessionProcesses } from "./sessionStory";
import { clampWindow, timelineTicks, zoomWindow } from "./timelineViewport";
import type { AnalyticsModel, EventSegment } from "./utils";

export type SessionTimelineHandle = {
  toggleProcess: (name: string) => void;
  resetZoom: () => void;
  restoreProcess: (name: string | null) => void;
};

type SessionTimelineProps = {
  model: AnalyticsModel;
  selectedStep?: { traceIndex: number; eventIndex: number } | null;
  onFocusProcess?: (name: string | null) => void;
  onZoomChange?: (zoomed: boolean) => void;
  onSelectRun?: (traceIndex: number) => void;
  onSelectEvent?: (event: EventSegment) => void;
};

// Diagonal hatch marking "away from keyboard" stretches over the process event
// bars they temporally overlap. The base is always the same light grey
// (independent of the process color), matching the idle slice in the donut chart.
const IDLE_HATCH =
  "repeating-linear-gradient(45deg, var(--color-surface-strong) 0 3px, var(--color-outline-strong) 3px 5px)";

type TimelineSegment = {
  start: number;
  dur: number;
  tooltip: string;
  background: string;
  opacity?: number;
  minWidthPct?: number;
  minWidthClass?: string;
  rounded?: boolean;
  selected?: boolean;
  onClick?: () => void;
};

type TimelineRowProps = {
  label: string;
  labelClassName?: string;
  labelStyle?: React.CSSProperties;
  labelContent?: React.ReactNode;
  segments: TimelineSegment[];
  bounds: { s0: number; rng: number };
  className?: string;
  minWidthPct?: number;
  minWidthClass?: string;
};

const renderTimelineSegment = (
  segment: TimelineSegment,
  index: number,
  bounds: { s0: number; rng: number },
  defaultMinWidthPct: number,
  defaultMinWidthClass: string,
  keyPrefix: string,
) => {
  if (segment.start + segment.dur < bounds.s0 || segment.start > bounds.s0 + bounds.rng)
    return null;
  const minWidthPct = segment.minWidthPct ?? defaultMinWidthPct;
  const minWidthClass = segment.minWidthClass ?? defaultMinWidthClass;
  const roundedClass = segment.rounded === false ? "" : "rounded-sm";
  const Segment = segment.onClick ? "button" : "div";
  const className = ["absolute", "h-full", roundedClass, minWidthClass].filter(Boolean).join(" ");

  return (
    <Tooltip.Root key={`${keyPrefix}-${index}`}>
      <Tooltip.Trigger asChild>
        <Segment
          type={segment.onClick ? "button" : undefined}
          aria-pressed={segment.onClick ? !!segment.selected : undefined}
          tabIndex={segment.onClick ? 0 : undefined}
          aria-label={segment.tooltip}
          onClick={segment.onClick}
          className={`${className} ${segment.selected ? "ring-2 ring-inset ring-text z-10" : ""} border-0 p-0 focus-visible:outline focus-visible:outline-2 focus-visible:outline-info ${segment.onClick ? "cursor-pointer hover:brightness-110" : "cursor-default"}`}
          style={{
            left: `${((segment.start - bounds.s0) / bounds.rng) * 100}%`,
            width: `${Math.max((segment.dur / bounds.rng) * 100, minWidthPct)}%`,
            background: segment.background,
            opacity: segment.opacity,
          }}
        />
      </Tooltip.Trigger>
      <Tooltip.Portal>
        <Tooltip.Content
          side="top"
          sideOffset={6}
          collisionPadding={12}
          className="timeline-tooltip"
        >
          {segment.tooltip}
        </Tooltip.Content>
      </Tooltip.Portal>
    </Tooltip.Root>
  );
};

const TimelineRow = ({
  label,
  labelClassName = "",
  labelStyle,
  labelContent,
  segments,
  bounds,
  className = "",
  minWidthPct = 0,
  minWidthClass = "min-w-[3px]",
}: TimelineRowProps) => (
  <div className={`flex items-center h-7 mb-1 ${className}`}>
    <div
      className={`w-[140px] @[600px]:w-[170px] @[800px]:w-[250px] text-xs leading-4 text-left pr-3 truncate shrink-0 ${labelClassName}`}
      style={labelStyle}
      title={label}
    >
      {labelContent ?? label}
    </div>

    <Tooltip.Provider delayDuration={0} skipDelayDuration={0}>
      <div className="flex-1 relative h-full overflow-hidden bg-surface-strong rounded-sm">
        {segments.map((seg, index) =>
          renderTimelineSegment(seg, index, bounds, minWidthPct, minWidthClass, "segment"),
        )}
      </div>
    </Tooltip.Provider>
  </div>
);

const SessionTimeline = forwardRef<SessionTimelineHandle, SessionTimelineProps>(
  function SessionTimeline(
    { model, onSelectEvent, onSelectRun, onFocusProcess, onZoomChange, selectedStep },
    ref,
  ) {
    const { t } = useTranslation();
    const bounds = useMemo(() => {
      const allStarts = model.allEvents.map(ev => ev.start);
      const allEnds = model.allEvents.map(ev => ev.end);

      model.idleSegments.forEach(seg => {
        allStarts.push(seg.start);
        allEnds.push(seg.end);
      });

      if (allStarts.length === 0 || allEnds.length === 0) {
        return { s0: 0, s1: 60, rng: 60 };
      }

      const s0 = Math.min(...allStarts) - 2;
      const s1 = Math.max(...allEnds) + 2;
      return { s0, s1, rng: Math.max(s1 - s0, 1) };
    }, [model.idleSegments, model.allEvents]);

    const [view, setView] = useState(bounds);
    const isZoomed = view.rng < bounds.rng;
    useEffect(() => {
      onZoomChange?.(isZoomed);
    }, [isZoomed, onZoomChange]);
    const [showAllProcesses, setShowAllProcesses] = useState(false);
    const [focusedProcessName, setFocusedProcessName] = useState<string | null>(null);
    const overviewView = useRef(bounds);
    const trackRef = useRef<HTMLDivElement>(null);
    const plotRef = useRef<HTMLDivElement>(null);
    const [width, setWidth] = useState(800);
    useEffect(() => {
      setView(bounds);
      setShowAllProcesses(false);
      setFocusedProcessName(null);
      overviewView.current = bounds;
    }, [bounds]);
    useEffect(() => {
      const track = trackRef.current;
      if (!track) return;
      const observer = new ResizeObserver(() => setWidth(track.getBoundingClientRect().width));
      observer.observe(track);
      setWidth(track.getBoundingClientRect().width);
      return () => observer.disconnect();
    }, []);
    useEffect(() => {
      const plot = plotRef.current;
      if (!plot) return;
      const wheel = (event: WheelEvent) => {
        const rect = trackRef.current?.getBoundingClientRect();
        if (!rect || event.clientX < rect.left) return;
        if (event.metaKey || event.ctrlKey) {
          event.preventDefault();
          setView(current => ({
            ...bounds,
            ...zoomWindow(
              current,
              bounds,
              Math.exp(Math.max(-100, Math.min(100, event.deltaY)) * 0.01),
              Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width)),
            ),
          }));
        } else if (Math.abs(event.deltaX) > Math.abs(event.deltaY) || event.shiftKey) {
          event.preventDefault();
          setView(current => ({
            ...bounds,
            ...clampWindow(
              {
                ...current,
                s0: current.s0 + ((event.deltaX || event.deltaY) / rect.width) * current.rng,
              },
              bounds,
            ),
          }));
        }
      };
      plot.addEventListener("wheel", wheel, { passive: false });
      return () => plot.removeEventListener("wheel", wheel);
    }, [bounds]);
    useEffect(() => {
      if (!selectedStep) return;
      const event = model.allEvents.find(
        ev =>
          ev.traceIndex === selectedStep.traceIndex &&
          (ev.eventIndex ?? 0) === selectedStep.eventIndex,
      );
      if (!event) return;
      setView(current => {
        if (event.start >= current.s0 && event.start <= current.s0 + current.rng) return current;
        return {
          ...bounds,
          ...clampWindow({ ...current, s0: event.start - current.rng / 2 }, bounds),
        };
      });
    }, [selectedStep, model.allEvents, bounds]);
    const timeLabels = timelineTicks(view, width);
    const processRows = useMemo(() => sessionProcesses(model), [model]);
    const focusedProcess = processRows.find(row => row.name === focusedProcessName);
    const visibleProcesses = showAllProcesses ? processRows : processRows.slice(0, 4);
    const returnToOverview = () => {
      setFocusedProcessName(null);
      setView(overviewView.current);
      onFocusProcess?.(null);
    };
    const toggleProcess = (name: string) => {
      if (focusedProcessName === name) {
        returnToOverview();
        return;
      }
      if (!focusedProcessName) overviewView.current = view;
      setFocusedProcessName(name);
      onFocusProcess?.(name);
    };
    useImperativeHandle(ref, () => ({
      toggleProcess,
      resetZoom: () => setView(bounds),
      restoreProcess: setFocusedProcessName,
    }));
    const eventSegments = (events: EventSegment[], color: string): TimelineSegment[] =>
      events.map((ev, index) => ({
        onClick: onSelectEvent ? () => onSelectEvent(ev) : undefined,
        selected:
          selectedStep?.traceIndex === ev.traceIndex &&
          selectedStep.eventIndex === (ev.eventIndex ?? 0),
        start: ev.start,
        dur: ev.dur,
        tooltip: `${ev.eventName}: ${ev.startStr} -> ${ev.endStr} (${formatDuration(ev.dur * 60)})`,
        background: color,
        opacity: index % 2 === 0 ? 0.95 : 0.65,
      }));

    const insignificantEvents = useMemo(
      () => model.allEvents.filter(ev => !model.processes[ev.traceIndex].trace.significance),
      [model.processes, model.allEvents],
    );

    // Idle time is the session span minus active coverage, so it lives in the gaps
    // the process rows leave behind. It gets its own dedicated row rather than an
    // overlay on the process bars (which it never overlaps by construction).
    const idleSegments = useMemo<TimelineSegment[]>(
      () =>
        model.idleSegments
          .filter(seg => seg.end > seg.start)
          .map(seg => ({
            start: seg.start,
            dur: seg.end - seg.start,
            tooltip: `${t("idle")}: ${seg.startLabel} -> ${seg.endLabel} (${formatDuration(seg.dur * 60)})`,
            background: IDLE_HATCH,
            rounded: false,
          })),
      [model.idleSegments, t],
    );

    return (
      <div className="pb-2">
        {focusedProcess && (
          <div className="mb-1 flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={returnToOverview}
              className="inline-flex items-center gap-1.5 rounded-lg border border-outline bg-surface px-3 py-2 text-sm font-medium text-text-mid hover:bg-muted-surface focus-visible:outline-info"
            >
              <ArrowLeft size={15} aria-hidden="true" />
              {t("sessionStory.allProcesses")}
            </button>
            <h3 className="min-w-0 text-sm font-medium" style={{ color: focusedProcess.color }}>
              <button
                type="button"
                aria-pressed={true}
                onClick={() => toggleProcess(focusedProcess.name)}
                className="rounded text-left hover:underline focus-visible:outline-info"
              >
                {focusedProcess.name}
              </button>
            </h3>
          </div>
        )}
        <div ref={plotRef} className="relative" onDoubleClick={() => setView(bounds)}>
          <div
            ref={trackRef}
            className="relative ml-[140px] @[600px]:ml-[170px] @[800px]:ml-[250px] mb-2 h-6"
            data-testid="timeline-axis"
          >
            {timeLabels.map(({ label, pos }) => (
              <span
                key={pos}
                className="absolute whitespace-nowrap text-[11px] text-text-dim"
                style={{ left: `${pos}%`, transform: "translateX(-50%)" }}
              >
                {label}
              </span>
            ))}
          </div>
          {focusedProcess ? (
            <div role="group" aria-label={focusedProcess.name}>
              {focusedProcess.runs.map((run, index) => (
                <TimelineRow
                  key={run.index}
                  label={`${focusedProcess.name} · ${t("sessionStory.run", { num: index + 1 })}`}
                  labelStyle={{ color: focusedProcess.color }}
                  bounds={view}
                  labelContent={
                    <button
                      type="button"
                      className="block w-full truncate text-left hover:underline focus-visible:outline-info"
                      onClick={() => onSelectRun?.(run.index)}
                    >
                      {t("sessionStory.run", { num: index + 1 })} ·{" "}
                      {formatDuration(run.pv.totalMin * 60)}
                    </button>
                  }
                  segments={eventSegments(
                    model.allEvents.filter(ev => ev.traceIndex === run.index),
                    focusedProcess.color,
                  )}
                />
              ))}
            </div>
          ) : (
            visibleProcesses.map(row => (
              <div key={row.name} role="group" aria-label={row.name}>
                <TimelineRow
                  label={row.name}
                  labelStyle={{ color: row.color }}
                  bounds={view}
                  labelContent={
                    <button
                      type="button"
                      className="block w-full truncate text-left hover:underline focus-visible:outline-info"
                      onClick={() => toggleProcess(row.name)}
                    >
                      {row.name}
                    </button>
                  }
                  segments={eventSegments(
                    model.allEvents.filter(ev => row.runs.some(run => run.index === ev.traceIndex)),
                    row.color,
                  )}
                />
              </div>
            ))
          )}
          {!focusedProcess && insignificantEvents.length > 0 && (
            <TimelineRow
              label={t("irrelevant_processes")}
              labelStyle={{ color: INSIGNIFICANT_COLOR }}
              bounds={view}
              segments={eventSegments(insignificantEvents, INSIGNIFICANT_COLOR)}
            />
          )}
          {!focusedProcess && idleSegments.length > 0 && (
            <TimelineRow
              label={t("idle")}
              labelClassName="text-text-dim italic"
              bounds={view}
              segments={idleSegments}
            />
          )}
        </div>
        {!focusedProcess && processRows.length > 4 && (
          <button
            type="button"
            aria-expanded={showAllProcesses}
            onClick={() => setShowAllProcesses(current => !current)}
            className="mt-3 inline-flex items-center gap-1.5 rounded text-sm font-medium text-info hover:underline focus-visible:outline-info"
          >
            {showAllProcesses
              ? t("sessionStory.showLess")
              : t("sessionStory.showAllProcesses", { count: processRows.length })}
            <ChevronDown
              size={14}
              aria-hidden="true"
              className={showAllProcesses ? "rotate-180" : ""}
            />
          </button>
        )}
      </div>
    );
  },
);

export default SessionTimeline;
